import type { SizingField, SizingFieldKey } from '@ispot/shared'

export type Confidence = 'hypothesis' | 'signal' | 'pattern' | 'validated'

export interface Assumption {
  id: string
  label: string
  value: string
  unit?: 'currency' | 'percent' | 'count' | 'none'
  confidence: Confidence
}

export interface Source {
  id: string
  kind: 'report' | 'interview' | 'desk' | 'benchmark'
  label: string
}

export interface DerivationInputs {
  formula: string
  assumptions: Assumption[]
  sources: Source[]
  /** Optional alternative derivation users can switch to (e.g. top-down vs bottom-up on SAM). */
  alternateLabel?: string
}

export interface Reconcile {
  topDown: number
  bottomUp: number
  diffPct: number
  withinTolerance: boolean
}

export const CONFIDENCE_META: Record<
  Confidence,
  { label: string; dot: string; bg: string; fg: string }
> = {
  hypothesis: {
    label: 'Hypothesis',
    dot: 'var(--color-text-muted)',
    bg: 'var(--color-warm-200)',
    fg: 'var(--color-text-secondary)',
  },
  signal: {
    label: 'Signal',
    dot: 'var(--color-business-500)',
    bg: 'var(--color-business-50)',
    fg: 'var(--color-business-700)',
  },
  pattern: {
    label: 'Pattern',
    dot: 'var(--color-solution-500)',
    bg: 'var(--color-solution-50)',
    fg: '#8a5f17',
  },
  validated: {
    label: 'Validated',
    dot: '#1F9D55',
    bg: '#e0f3e8',
    fg: '#157243',
  },
}

/** Map existing source + counts onto Hypothesis / Signal / Pattern / Validated. */
export function deriveConfidence(args: {
  source: SizingField['source']
  value: number | null
  sourceCount: number
  reconciled?: boolean
  validated?: boolean
}): Confidence {
  if (args.validated) return 'validated'
  if (args.value === null) return 'hypothesis'
  if (args.reconciled && args.sourceCount >= 1) return 'pattern'
  if (args.sourceCount >= 2) return 'pattern'
  if (args.sourceCount === 1) return 'signal'
  return 'hypothesis'
}

/**
 * Top-down vs bottom-up SAM reconciliation. Tolerance defaults to 25% which
 * matches the "within 25%" pill shown in the sizing header.
 */
export function computeReconcile(
  topDown: number | null,
  bottomUp: number | null,
  tolerancePct = 25,
): Reconcile | null {
  if (topDown == null || bottomUp == null) return null
  if (topDown === 0 && bottomUp === 0) return null
  const base = Math.max(Math.abs(topDown), Math.abs(bottomUp))
  const diffPct = Math.round((Math.abs(topDown - bottomUp) / base) * 100)
  return {
    topDown,
    bottomUp,
    diffPct,
    withinTolerance: diffPct <= tolerancePct,
  }
}

export function formatCurrency(v: number | null): string {
  if (v === null) return '—'
  if (Math.abs(v) >= 1e9) return `€${(v / 1e9).toFixed(1)}B`
  if (Math.abs(v) >= 1e6) return `€${(v / 1e6).toFixed(1)}M`
  if (Math.abs(v) >= 1e3) return `€${(v / 1e3).toFixed(1)}k`
  return `€${v}`
}

export function formatValue(v: number | null, unit: 'currency' | 'percent'): string {
  if (v === null) return '—'
  return unit === 'percent' ? `${v}%` : formatCurrency(v)
}

export const REGION_OPTIONS = [
  { id: 'global', label: 'Global' },
  { id: 'eu-27', label: 'EU-27' },
  { id: 'uk', label: 'UK' },
  { id: 'benelux', label: 'Benelux' },
  { id: 'us', label: 'US' },
  { id: 'apac', label: 'APAC' },
] as const
export type RegionId = (typeof REGION_OPTIONS)[number]['id']

export const HORIZON_OPTIONS = ['Y1', 'Y3', 'Y5'] as const
export type Horizon = (typeof HORIZON_OPTIONS)[number]

export interface SizingScope {
  regions: RegionId[]
  baseYear: number
  horizon: Horizon
}

export const DEFAULT_SCOPE: SizingScope = {
  regions: ['global'],
  baseYear: new Date().getFullYear(),
  horizon: 'Y3',
}

export const SIZING_ROW_LABEL: Record<SizingFieldKey, string> = {
  tam: 'TAM',
  samTopDown: 'SAM (top-down)',
  samBottomUp: 'SAM (bottom-up)',
  som: 'SOM',
  cagr: 'CAGR',
}

export const SIZING_ROW_UNIT: Record<SizingFieldKey, 'currency' | 'percent'> = {
  tam: 'currency',
  samTopDown: 'currency',
  samBottomUp: 'currency',
  som: 'currency',
  cagr: 'percent',
}

export function stringifyForInput(v: number | null, unit: 'currency' | 'percent'): string {
  if (v === null) return ''
  if (unit === 'percent') return String(v)
  if (Math.abs(v) >= 1e9) return `${trimZeros(v / 1e9)}B`
  if (Math.abs(v) >= 1e6) return `${trimZeros(v / 1e6)}M`
  if (Math.abs(v) >= 1e3) return `${trimZeros(v / 1e3)}k`
  return String(v)
}

export function parseShorthand(raw: string, unit: 'currency' | 'percent'): number | null {
  const trimmed = raw.trim()
  if (trimmed === '') return null
  const cleaned = trimmed.replace(/[€$£,\s]/g, '').replace(/%$/, '').toLowerCase()
  const match = cleaned.match(/^(-?\d+(?:\.\d+)?)([kmbt])?$/)
  if (!match) return null
  const base = parseFloat(match[1])
  if (!Number.isFinite(base)) return null
  const mult =
    match[2] === 't' ? 1e12 :
    match[2] === 'b' ? 1e9 :
    match[2] === 'm' ? 1e6 :
    match[2] === 'k' ? 1e3 : 1
  const value = base * mult
  if (unit === 'percent') return Math.max(0, Math.min(100, value))
  return Math.max(0, value)
}

function trimZeros(n: number): string {
  return parseFloat(n.toFixed(2)).toString()
}
